/**
 * World: a level compiled for fast physics and rendering queries.
 *
 * Splits LevelData.objects into solids (blocks), hazards (spike hitboxes) and
 * triggers (pads, orbs, portals, pickups), each with an x-interval index. Built once
 * per level (cheap: linear in the number of objects) and then shared read-only by
 * the player simulation, the renderer, bots and the level solver.
 *
 * What moves is kept apart, as functions of the level's tick (see game/motion.ts): moving
 * blocks (`movers`), the round hazards — saw blades, still or moving, and rotors
 * (`rounds`) — and a boss's attacks, there for a while only (`attacks`), each indexed by the
 * x range it sweeps.
 */
import type { BoostType } from '../boosts';
import type {
  AttackObject,
  LevelData,
  LevelObject,
  Motion,
  OrbColor,
  PadColor,
  PortalType,
  SpikeObject,
} from '../level/types';
import { ORB_REACH, SPIKE_HITBOX } from './constants';
import {
  attackBox,
  motionOffset,
  type Offset,
  rotorSpokes,
  rotorTurn,
  sawRadius,
  sweptBounds,
  turnCos,
  turnSin,
} from './motion';
import { XIndex } from './xIndex';

/** Axis-aligned box: x1 < x2, y1 < y2. */
export interface Aabb {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface TriggerBase extends Aabb {
  /** Index of the source object in LevelData.objects. */
  object: number;
}

/**
 * Something that reacts to the player instead of blocking them. The box is the
 * activation area (orbs reach ORB_REACH beyond their cell).
 */
export type Trigger =
  | (TriggerBase & { kind: 'pad'; color: PadColor })
  | (TriggerBase & { kind: 'orb'; color: OrbColor })
  /** Portals fire when the player's centre crosses `centerX`. */
  | (TriggerBase & { kind: 'portal'; portal: PortalType; centerX: number })
  | (TriggerBase & { kind: 'pickup'; boost: BoostType });

export interface WorldOptions {
  /**
   * Whether pickups become triggers (default true). The level solver leaves them out:
   * they never influence movement, and fewer triggers keep its search smaller.
   */
  pickups?: boolean;
}

/** The forgiving hitbox of a spike: a strip in the middle of its base, pointing to the tip. */
export function spikeHitbox(spike: Pick<SpikeObject, 'x' | 'y' | 'w' | 'h' | 'dir'>): Aabb {
  const { x, y, w, h, dir } = spike;
  if (dir === 'up' || dir === 'down') {
    const cx = x + w / 2;
    const half = (w * SPIKE_HITBOX.width) / 2;
    const depth = h * SPIKE_HITBOX.height;
    return dir === 'up'
      ? { x1: cx - half, y1: y, x2: cx + half, y2: y + depth }
      : { x1: cx - half, y1: y + h - depth, x2: cx + half, y2: y + h };
  }
  const cy = y + h / 2;
  const half = (h * SPIKE_HITBOX.width) / 2;
  const depth = w * SPIKE_HITBOX.height;
  // 'right' = tip points right, so the base is the cell's left side.
  return dir === 'right'
    ? { x1: x, y1: cy - half, x2: x + depth, y2: cy + half }
    : { x1: x + w - depth, y1: cy - half, x2: x + w, y2: cy + half };
}

/** A moving block, compiled: its cell in the middle of its motion, and the motion. */
export interface MovingSolid extends Aabb {
  /** Index of the source object in LevelData.objects. */
  readonly object: number;
  readonly move: Motion;
}

/**
 * A saw blade or a rotor, compiled: the circles that kill (see roundCircles). A saw is one
 * circle round its disc's centre (moved by its motion, if any); a rotor has `arms` arms, each
 * with circles at `spokes` from the pivot, turning once per |period| ticks.
 */
export interface RoundHazard {
  /** Index of the source object in LevelData.objects. */
  readonly object: number;
  /** A saw's centre in the middle of its motion, or a rotor's pivot. */
  readonly cx: number;
  readonly cy: number;
  /** Radius of each deadly circle. */
  readonly radius: number;
  /** A saw's motion (null: it stands still, or it is a rotor). */
  readonly move: Motion | null;
  /** A rotor's arms (0 for a saw), its turn and its circles' distances from the pivot. */
  readonly arms: number;
  readonly period: number;
  readonly phase: number;
  readonly spokes: Float64Array;
  /** Whether it is anywhere else at another tick (a moving saw, any rotor). */
  readonly moving: boolean;
}

/** Most circles one round hazard is made of (4 arms of a bar reaching 4.5 blocks). */
export const MAX_ROUND_CIRCLES = 64;

/** Ticks of boxes and circles a World remembers per moving thing (a power of two). */
const CACHED = 16;

/** Packed boxes of one object category (struct of arrays: fast to scan in the physics loop). */
export class BoxSet {
  readonly count: number;
  readonly x1: Float64Array;
  readonly y1: Float64Array;
  readonly x2: Float64Array;
  readonly y2: Float64Array;
  /** Index of the source object in LevelData.objects. */
  readonly object: Int32Array;
  readonly index: XIndex;

  constructor(boxes: ReadonlyArray<Aabb & { object: number }>) {
    this.count = boxes.length;
    this.x1 = Float64Array.from(boxes, (b) => b.x1);
    this.y1 = Float64Array.from(boxes, (b) => b.y1);
    this.x2 = Float64Array.from(boxes, (b) => b.x2);
    this.y2 = Float64Array.from(boxes, (b) => b.y2);
    this.object = Int32Array.from(boxes, (b) => b.object);
    this.index = new XIndex(this.x1, this.x2);
  }
}

export class World {
  readonly objects: readonly LevelObject[];
  /** x of the finish line. */
  readonly length: number;
  /** Blocks that stand still. */
  readonly solids: BoxSet;
  /** Spike hitboxes. */
  readonly hazards: BoxSet;
  readonly triggers: readonly Trigger[];
  readonly triggerIndex: XIndex;
  /** Moving blocks, and an index of the x range each sweeps. */
  readonly movers: readonly MovingSolid[];
  readonly moverIndex: XIndex;
  /** Saw blades and rotors, and an index of the x range each sweeps. */
  readonly rounds: readonly RoundHazard[];
  readonly roundIndex: XIndex;
  /**
   * A boss's attacks, and an index of the x range each sweeps; `attackObject[i]` is the index of
   * attack i in `objects`.
   */
  readonly attacks: readonly AttackObject[];
  readonly attackObject: Int32Array;
  readonly attackIndex: XIndex;
  /** Whether anything in the level moves or turns (a moving block, saw or any rotor), or comes and goes (an attack). */
  readonly dynamic: boolean;
  /**
   * The boxes the moving blocks and the round hazards sweep, by index (their indices' items: x;
   * low and high: y). Every position is within, at every tick.
   */
  readonly moverFrom: Float64Array;
  readonly moverTo: Float64Array;
  readonly moverLow: Float64Array;
  readonly moverHigh: Float64Array;
  readonly roundFrom: Float64Array;
  readonly roundTo: Float64Array;
  readonly roundLow: Float64Array;
  readonly roundHigh: Float64Array;
  readonly attackFrom: Float64Array;
  readonly attackTo: Float64Array;
  readonly attackLow: Float64Array;
  readonly attackHigh: Float64Array;
  private readonly objectIndex: XIndex;
  /** What each object may occupy at any tick (its cell, but for moving ones; see sweptBounds). */
  private readonly bounds: readonly Aabb[];
  private readonly offset: Offset = { x: 0, y: 0 };
  /**
   * The last CACHED ticks' boxes and circles of every moving thing (the solver asks for the same
   * tick of a layer thousands of times; whole ticks only): the tick in each slot, and its data.
   */
  private readonly moverTicks: Float64Array;
  private readonly moverBoxes: Float64Array;
  private readonly roundTicks: Float64Array;
  private readonly roundData: Float64Array;
  /** Where each round hazard's cached circles begin in roundData. */
  private readonly roundAt: Int32Array;

  constructor(level: Pick<LevelData, 'objects' | 'length'>, options: WorldOptions = {}) {
    const includePickups = options.pickups ?? true;
    this.objects = level.objects;
    this.length = level.length;
    const solids: Array<Aabb & { object: number }> = [];
    const hazards: Array<Aabb & { object: number }> = [];
    const triggers: Trigger[] = [];
    const movers: MovingSolid[] = [];
    const rounds: RoundHazard[] = [];
    const attacks: AttackObject[] = [];
    const attackObject: number[] = [];
    level.objects.forEach((o, object) => {
      const cell = { x1: o.x, y1: o.y, x2: o.x + o.w, y2: o.y + o.h, object };
      switch (o.type) {
        case 'attack':
          attacks.push(o);
          attackObject.push(object);
          break;
        case 'block':
          if (o.move) movers.push({ ...cell, move: o.move });
          else solids.push(cell);
          break;
        case 'spike':
          hazards.push({ ...spikeHitbox(o), object });
          break;
        case 'saw':
          rounds.push({
            object,
            cx: o.x + o.w / 2,
            cy: o.y + o.h / 2,
            radius: sawRadius(o.w),
            move: o.move ?? null,
            arms: 0,
            period: 1,
            phase: 0,
            spokes: new Float64Array(0),
            moving: o.move !== undefined,
          });
          break;
        case 'rotor': {
          const spokes = rotorSpokes(o.arm, o.w / 2);
          const arms = Math.max(1, Math.min(4, Math.round(o.arms)));
          rounds.push({
            object,
            cx: o.x + o.w / 2,
            cy: o.y + o.h / 2,
            radius: spokes.radius,
            move: null,
            arms,
            period: o.period,
            phase: o.phase,
            // (The outermost circles: a rotor larger than any level has keeps its tips.)
            spokes: Float64Array.from(
              spokes.distances.slice(0, Math.floor(MAX_ROUND_CIRCLES / arms)),
            ),
            moving: true,
          });
          break;
        }
        case 'pad':
          triggers.push({ ...cell, kind: 'pad', color: o.color });
          break;
        case 'orb':
          triggers.push({
            kind: 'orb',
            color: o.color,
            object,
            x1: cell.x1 - ORB_REACH,
            y1: cell.y1 - ORB_REACH,
            x2: cell.x2 + ORB_REACH,
            y2: cell.y2 + ORB_REACH,
          });
          break;
        case 'portal':
          triggers.push({ ...cell, kind: 'portal', portal: o.portal, centerX: o.x + o.w / 2 });
          break;
        case 'pickup':
          if (includePickups) triggers.push({ ...cell, kind: 'pickup', boost: o.boost });
          break;
      }
    });
    this.solids = new BoxSet(solids);
    this.hazards = new BoxSet(hazards);
    this.triggers = triggers;
    this.triggerIndex = new XIndex(
      triggers.map((t) => t.x1),
      triggers.map((t) => t.x2),
    );
    this.movers = movers;
    this.moverFrom = Float64Array.from(movers, (m) => m.x1 - Math.abs(m.move.dx));
    this.moverTo = Float64Array.from(movers, (m) => m.x2 + Math.abs(m.move.dx));
    this.moverLow = Float64Array.from(movers, (m) => m.y1 - Math.abs(m.move.dy));
    this.moverHigh = Float64Array.from(movers, (m) => m.y2 + Math.abs(m.move.dy));
    this.moverIndex = new XIndex(this.moverFrom, this.moverTo);
    this.rounds = rounds;
    // How far a round hazard's circles reach from its centre (a saw's motion added).
    const reachX = (r: RoundHazard) =>
      r.arms > 0 ? (r.spokes[0] ?? 0) + r.radius : r.radius + Math.abs(r.move?.dx ?? 0);
    const reachY = (r: RoundHazard) =>
      r.arms > 0 ? (r.spokes[0] ?? 0) + r.radius : r.radius + Math.abs(r.move?.dy ?? 0);
    this.roundFrom = Float64Array.from(rounds, (r) => r.cx - reachX(r));
    this.roundTo = Float64Array.from(rounds, (r) => r.cx + reachX(r));
    this.roundLow = Float64Array.from(rounds, (r) => r.cy - reachY(r));
    this.roundHigh = Float64Array.from(rounds, (r) => r.cy + reachY(r));
    this.roundIndex = new XIndex(this.roundFrom, this.roundTo);
    this.moverTicks = new Float64Array(movers.length * CACHED).fill(Number.NaN);
    this.moverBoxes = new Float64Array(movers.length * CACHED * 4);
    this.roundTicks = new Float64Array(rounds.length * CACHED).fill(Number.NaN);
    this.roundAt = new Int32Array(rounds.length + 1);
    rounds.forEach((r, i) => {
      const circles = r.arms > 0 ? r.arms * r.spokes.length : 1;
      this.roundAt[i + 1] = this.roundAt[i]! + circles * 3 * CACHED;
    });
    this.roundData = new Float64Array(this.roundAt[rounds.length]!);
    this.attacks = attacks;
    this.attackObject = Int32Array.from(attackObject);
    const swept = attacks.map(sweptBounds);
    this.attackFrom = Float64Array.from(swept, (b) => b.x1);
    this.attackTo = Float64Array.from(swept, (b) => b.x2);
    this.attackLow = Float64Array.from(swept, (b) => b.y1);
    this.attackHigh = Float64Array.from(swept, (b) => b.y2);
    this.attackIndex = new XIndex(this.attackFrom, this.attackTo);
    this.dynamic = movers.length > 0 || rounds.some((r) => r.moving) || attacks.length > 0;
    const bounds = level.objects.map(sweptBounds);
    this.objectIndex = new XIndex(
      bounds.map((b) => b.x1),
      bounds.map((b) => b.x2),
    );
    this.bounds = bounds;
  }

  /**
   * Indices (into `objects`, ascending) of the objects that may be within the x range
   * [x1, x2] — e.g. everything inside the camera view (moving ones wherever they are on
   * their way). Appends to `out` (pass a reused array to draw every frame without allocating).
   */
  objectsInRange(x1: number, x2: number, out: number[] = []): number[] {
    const n = this.objectIndex.query(x1, x2);
    // The candidates are a scratch buffer: sort them in place (typed arrays sort numerically).
    const found = this.objectIndex.found.subarray(0, n).sort();
    for (let i = 0; i < n; i++) {
      const id = found[i]!;
      const b = this.bounds[id]!;
      if (b.x1 <= x2 && b.x2 >= x1) out.push(id);
    }
    return out;
  }

  /** Whether a moving block may be within [x1, x2] at some tick (the x range it sweeps). */
  moverWithin(x1: number, x2: number): boolean {
    const index = this.moverIndex;
    const n = index.query(x1, x2);
    for (let i = 0; i < n; i++) {
      const id = index.found[i]!;
      if (this.moverFrom[id]! <= x2 && this.moverTo[id]! >= x1) return true;
    }
    return false;
  }

  /**
   * Whether a saw or a rotor may be within [x1, x2] at some tick — of those that move or turn
   * only, or rotors only, if asked.
   */
  roundWithin(x1: number, x2: number, which: 'any' | 'moving' | 'rotor' = 'any'): boolean {
    const index = this.roundIndex;
    const n = index.query(x1, x2);
    for (let i = 0; i < n; i++) {
      const id = index.found[i]!;
      if (this.roundFrom[id]! > x2 || this.roundTo[id]! < x1) continue;
      const r = this.rounds[id]!;
      if (which === 'any' || (which === 'moving' ? r.moving : r.arms > 0)) return true;
    }
    return false;
  }

  /**
   * Whether an attack may be within [x1, x2] while it is there at some tick of [t1, t2] (the x
   * range it sweeps, the ticks it is there).
   */
  attackWithin(
    x1: number,
    x2: number,
    t1: number = Number.NEGATIVE_INFINITY,
    t2: number = Number.POSITIVE_INFINITY,
  ): boolean {
    const index = this.attackIndex;
    const n = index.query(x1, x2);
    for (let i = 0; i < n; i++) {
      const id = index.found[i]!;
      if (this.attackFrom[id]! > x2 || this.attackTo[id]! < x1) continue;
      const a = this.attacks[id]!;
      if (a.from <= t2 && a.to > t1) return true;
    }
    return false;
  }

  /**
   * Where attack `i` is at `tick` (written into `out`; renderers may pass a fractional tick);
   * false while it is not there.
   */
  attackBox(i: number, tick: number, out: Aabb): boolean {
    return attackBox(this.attacks[i]!, tick, out);
  }

  /** Where moving block `i` is at `tick` (written into `out`; renderers may pass a fractional tick). */
  moverBox(i: number, tick: number, out: Aabb): Aabb {
    const whole = Number.isInteger(tick);
    const slot = i * CACHED + (tick & (CACHED - 1));
    const cache = this.moverBoxes;
    if (whole && this.moverTicks[slot] === tick) {
      out.x1 = cache[4 * slot]!;
      out.y1 = cache[4 * slot + 1]!;
      out.x2 = cache[4 * slot + 2]!;
      out.y2 = cache[4 * slot + 3]!;
      return out;
    }
    const m = this.movers[i]!;
    const d = motionOffset(m.move, tick, this.offset);
    out.x1 = m.x1 + d.x;
    out.y1 = m.y1 + d.y;
    out.x2 = m.x2 + d.x;
    out.y2 = m.y2 + d.y;
    if (whole) {
      this.moverTicks[slot] = tick;
      cache[4 * slot] = out.x1;
      cache[4 * slot + 1] = out.y1;
      cache[4 * slot + 2] = out.x2;
      cache[4 * slot + 3] = out.y2;
    }
    return out;
  }

  /**
   * The deadly circles of round hazard `i` at `tick`, as (x, y, radius) triples written into
   * `out` (room for MAX_ROUND_CIRCLES); returns how many.
   */
  roundCircles(i: number, tick: number, out: Float64Array): number {
    const r = this.rounds[i]!;
    const count = r.arms > 0 ? r.arms * r.spokes.length : 1;
    if (!Number.isInteger(tick)) return this.circlesAt(r, tick, out);
    const slot = i * CACHED + (tick & (CACHED - 1));
    const from = this.roundAt[i]! + (tick & (CACHED - 1)) * count * 3;
    const cache = this.roundData;
    if (this.roundTicks[slot] !== tick) {
      this.circlesAt(r, tick, out);
      for (let k = 0; k < 3 * count; k++) cache[from + k] = out[k]!;
      this.roundTicks[slot] = tick;
      return count;
    }
    for (let k = 0; k < 3 * count; k++) out[k] = cache[from + k]!;
    return count;
  }

  /** roundCircles, computed. */
  private circlesAt(r: RoundHazard, tick: number, out: Float64Array): number {
    if (r.arms === 0) {
      let x = r.cx;
      let y = r.cy;
      if (r.move !== null) {
        const d = motionOffset(r.move, tick, this.offset);
        x += d.x;
        y += d.y;
      }
      out[0] = x;
      out[1] = y;
      out[2] = r.radius;
      return 1;
    }
    let n = 0;
    for (let k = 0; k < r.arms; k++) {
      const turn = rotorTurn(r.period, r.phase, r.arms, k, tick);
      const cos = turnCos(turn);
      const sin = turnSin(turn);
      for (let j = 0; j < r.spokes.length; j++) {
        const d = r.spokes[j]!;
        out[3 * n] = r.cx + d * cos;
        out[3 * n + 1] = r.cy + d * sin;
        out[3 * n + 2] = r.radius;
        n++;
      }
    }
    return n;
  }
}
